import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import { Box, Card, Typography } from "@mui/material";
import {
  CreatedAtOrganizer,
  Divider,
  Organizer,
  PeriodContainer,
  StyledBox,
  Title,
} from "./show-request-card-examiner.style";
import {
  useMyHrEmployee,
  useMyHrEmployee as useMyHrEmployeeDI,
} from "my-hr/hooks";
import { MyTimeRequestTypeInterface } from "my-time/components";
import {
  MY_TIME_APPROVE_REQUEST,
  MY_TIME_DELETE_APPROVED_REQUEST,
  MY_TIME_REJECT_REQUEST,
} from "my-time/graphql";
import { myTimeMessageState } from "my-time/states/my-time-message";
import {
  OcAvatar,
  OcCalendarDay,
  OcCardButtonBar,
  OcRequestsMessagesCard,
  OcStateChip,
} from "oc/components";
import { useOcSnackbar } from "oc/hooks";
import { getFullname } from "oc/utils";
import { MY_TIME_GET_ALL_REQUESTS_BADGE_COUNTER } from "my-time/graphql/queries";
import { useMyTimeActualDate } from "my-time/hooks";

interface MyTimeShowRequestCardExaminerProps {
  data: any;
  messages: any;
  loading: boolean;
  error?: any;
  employeeCode: string;
  contractCode: string;
  isMobile: boolean;
  refetch: () => void;
}

export const MyTimeShowRequestCardExaminer = ({
  data,
  messages,
  loading,
  error,
  employeeCode,
  contractCode,
  isMobile,
  refetch,
}: MyTimeShowRequestCardExaminerProps) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const { setOpenMessage } = useOcSnackbar();
  const message = useRecoilValue(myTimeMessageState);
  const clearMessageState = useSetRecoilState(myTimeMessageState);

  const { date } = useMyTimeActualDate();
  const startDate = moment(date).startOf("month").toDate();
  const endDate = moment(date).endOf("month").toDate();

  // Track mutation loading state
  const [isApproving, setIsApproving] = React.useState(false);
  const [isRejecting, setIsRejecting] = React.useState(false);

  const { data: employeeData } = useMyHrEmployeeDI({
    employeeId: employeeCode,
  });

  const locale = i18n.language.substr(0, 2).toLowerCase();

  const formatDateTime = (
    dateTime: string | Date | undefined,
    locale: string,
  ) => {
    return moment(dateTime).locale(locale).format("YYYY. MM. DD. HH:mm");
  };
  let createdAt = formatDateTime(data?.createdAt, locale);
  let startTime = formatDateTime(data?.startTime, locale);
  let endTime = formatDateTime(data?.endTime, locale);

  let disabled = false;
  if (data?.state !== "requested") {
    disabled = true;
  }

  const handleCancel = () => {
    history.goBack();
  };

  let QUERY = MY_TIME_REJECT_REQUEST;
  if (data?.state === "approved") {
    QUERY = MY_TIME_DELETE_APPROVED_REQUEST;
  }

  const [approveRequest] = useMutation(MY_TIME_APPROVE_REQUEST, {
    refetchQueries: [
      {
        query: MY_TIME_GET_ALL_REQUESTS_BADGE_COUNTER,
        variables: { startDate, endDate },
      },
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      setIsApproving(false);
      const res = data?.approveMyTimeRequest;
      if (res?.result) {
        setOpenMessage({
          type: "success",
          message: t("MY_TIME_APPROVE_SUCCEEDED"),
        });
        clearMessageState("");
        refetch();
        history.goBack();
      } else {
        setOpenMessage({
          type: "error",
          message: res?.message || t("MY_TIME_APPROVE_FAILED"),
        });
        clearMessageState("");
      }
    },
    onError: () => {
      setIsApproving(false);
      setOpenMessage({
        type: "error",
        message: t("MY_TIME_APPROVE_FAILED"),
      });
    },
  });

  const [rejectRequest] = useMutation(QUERY, {
    refetchQueries: [
      {
        query: MY_TIME_GET_ALL_REQUESTS_BADGE_COUNTER,
        variables: { startDate, endDate },
      },
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      setIsRejecting(false);
      let res = data?.rejectMyTimeRequest;
      if (data?.deleteApprovedMyTimeRequest) {
        res = data?.deleteApprovedMyTimeRequest;
      }
      if (res?.result) {
        setOpenMessage({
          type: "success",
          message: t("MY_TIME_REJECT_SUCCEEDED"),
        });
        clearMessageState("");
        refetch();
        history.goBack();
      } else {
        setOpenMessage({
          type: "error",
          message: res?.message || t("MY_TIME_REJECT_FAILED"),
        });
        clearMessageState("");
      }
    },
    onError: () => {
      setIsRejecting(false);
      setOpenMessage({
        type: "error",
        message: t("MY_TIME_REJECT_FAILED"),
      });
    },
  });

  const handleDelete = () => {
    setIsRejecting(true);
    let params: any = { _id: data?._id, message };
    if (data?.state === "approved") {
      params = {
        contractCode: contractCode,
        myTimeType: data?.myTimeType,
        startTime: data?.startTime,
        endTime: data?.endTime,
        message: message,
      };
    }
    rejectRequest({ variables: params });
  };

  const handleApprove = () => {
    setIsApproving(true);
    const params = {
      _id: data?._id,
      message: message,
    };
    approveRequest({ variables: params });
  };

  const { data: approvedCode } = useMyHrEmployee({
    employeeId: data?.approvedBy,
  });
  const { data: rejectedCode } = useMyHrEmployee({
    employeeId: data?.rejectedBy,
  });

  return (
    <Card
      variant="outlined"
      style={{
        borderRadius: "4px",
        borderColor: error ? "#cc0033" : "white",
        overflow: "visible",
      }}
      elevation={0}
    >
      <StyledBox isMobile={isMobile}>
        <Title>{t("OC_REQUEST")}</Title>
        <Box height="24px" />
        <Divider />
        <Box height="24px" />
        <Box
          display="flex"
          flexDirection={isMobile ? "column-reverse" : "unset"}
        >
          {!isMobile && (
            <>
              <Organizer>
                <OcAvatar
                  employee={employeeCode}
                  style={{
                    width: "100px",
                    height: "100px",
                  }}
                />
                <Box width="16px" />
              </Organizer>
            </>
          )}
          <Box width="100%">
            <Organizer>
              <Typography variant="h6">{getFullname(employeeData)}</Typography>
              <CreatedAtOrganizer>
                <Box fontSize="9px" style={{ color: "#919699" }}>
                  {createdAt}
                </Box>
                <Box height="2px" />
                {data?.state === "approved" && (
                  <Box
                    fontSize="9px"
                    paddingTop="1px"
                    style={{ color: "#919699" }}
                  >
                    {getFullname(approvedCode)}
                  </Box>
                )}
                {data?.state === "rejected" && (
                  <Box
                    fontSize="9px"
                    paddingTop="1px"
                    style={{ color: "#919699" }}
                  >
                    {getFullname(rejectedCode)}
                  </Box>
                )}
                <Box height="2px" />
                <OcStateChip
                  label={!loading && t(`MY_LEAVE_${data?.state.toUpperCase()}`)}
                  state={data?.state}
                  loading={loading}
                  variant="body1"
                />
              </CreatedAtOrganizer>
            </Organizer>
            <Box height="22px" />
            <Box display="flex" alignItems={isMobile ? "center" : "unset"}>
              <OcCalendarDay
                date={data?.hours}
                loading={loading}
                hours={true}
                unPaidDate={false}
              />
              <Box width="16px" />
              <PeriodContainer isMobile={isMobile}>
                <Typography>{t("OC_PERIOD")}</Typography>
                <Box>
                  {startTime} - {endTime}
                </Box>
              </PeriodContainer>
            </Box>
            {isMobile && <Box height="20px" />}
          </Box>
        </Box>
        {!isMobile && <Box height="24px" />}
        <MyTimeRequestTypeInterface
          data={data}
          myTimeType={data?.myTimeType}
          timeDataTypeName={data?.timeDataTypeName}
          unPaidDate={false}
        />
        <Box height="24px" />
        <Box>
          {!loading && (
            <OcRequestsMessagesCard
              messages={messages}
              module="MyTime"
              loading={loading}
              data={data}
              disabled={disabled}
              role="examiner"
            />
          )}
        </Box>
        <OcCardButtonBar
          leftButtonText={t("OC_REJECT")}
          leftButtonColor={data?.state !== "rejected" ? "primary" : undefined}
          leftButtonDisabled={loading || isApproving || isRejecting}
          handleLeftButton={handleDelete}
          leftButtonId="request-page-delete-button"
          rightButtonText={t("OC_CANCEL")}
          rightButtonColor="secondary"
          rightButtonDisabled={loading || isApproving || isRejecting}
          handleRightButton={handleCancel}
          rightButtonId="cancel-button"
          primaryButtonText={t("OC_APPROVE")}
          handlePrimaryButton={handleApprove}
          primaryButtonDisabled={loading || disabled || isApproving || isRejecting}
          primaryButtonId="approve-button"
        />
      </StyledBox>
    </Card>
  );
};

export default MyTimeShowRequestCardExaminer;
